import { Slot } from '@allara-health/source-health-client'
import { EnhancedProvider } from 'containers/Scheduling/types'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import {
  PHASES,
  PENALTY_LEVELS,
  BUTTON_TEXT,
  TIME_CATEGORIES,
  TIME_RANGES,
  SLOT_CONSTANTS,
} from './constants'
import {
  PenaltyLevel,
  PenaltySlotCounts,
  ProviderSlotData,
  TimeBasedSlotData,
  TimeOfDaySlotCounts,
} from './types'

/**
 * Creates a date key for tracking expanded providers
 */
export const createDateKey = (
  selectedDate: Date,
  timeZone?: string
): string => {
  return timeZone
    ? format(utcToZonedTime(selectedDate, timeZone), 'yyyy-MM-dd')
    : format(selectedDate, 'yyyy-MM-dd')
}

/**
 * Categorizes a slot by time of day based on user's timezone
 */
export const getTimeOfDay = (
  slot: Slot,
  timeZone?: string
): 'morning' | 'afternoon' | 'evening' => {
  const slotDate = new Date(slot.start_at)
  const zonedTime = timeZone ? utcToZonedTime(slotDate, timeZone) : slotDate
  const hour = zonedTime.getHours()

  if (hour >= TIME_RANGES.MORNING_START && hour < TIME_RANGES.AFTERNOON_START) {
    return TIME_CATEGORIES.MORNING
  } else if (
    hour >= TIME_RANGES.AFTERNOON_START &&
    hour < TIME_RANGES.EVENING_START
  ) {
    return TIME_CATEGORIES.AFTERNOON
  } else {
    return TIME_CATEGORIES.EVENING
  }
}

/**
 * Categorizes slots by time of day and penalty level
 */
export const categorizeSlotsByTime = (
  provider: EnhancedProvider,
  timeZone?: string
): TimeBasedSlotData => {
  const timeBasedSlots: TimeBasedSlotData = {
    morning: {
      noPenalty: [],
      mediumPenalty: [],
      highPenalty: [],
    },
    afternoon: {
      noPenalty: [],
      mediumPenalty: [],
      highPenalty: [],
    },
    evening: {
      noPenalty: [],
      mediumPenalty: [],
      highPenalty: [],
    },
  }

  // Categorize no penalty slots
  provider.noPenaltySlots.forEach((slot) => {
    const timeOfDay = getTimeOfDay(slot, timeZone)

    timeBasedSlots[timeOfDay].noPenalty.push(slot)
  })

  // Categorize medium penalty slots
  provider.mediumPenaltySlots.forEach((slot) => {
    const timeOfDay = getTimeOfDay(slot, timeZone)
    timeBasedSlots[timeOfDay].mediumPenalty.push(slot)
  })

  // Categorize high penalty slots
  provider.highPenaltySlots.forEach((slot) => {
    const timeOfDay = getTimeOfDay(slot, timeZone)
    timeBasedSlots[timeOfDay].highPenalty.push(slot)
  })

  return timeBasedSlots
}

/**
 * Counts slots by time of day
 */
export const getTimeOfDayCounts = (
  timeBasedSlots: TimeBasedSlotData
): TimeOfDaySlotCounts => {
  return {
    morning:
      timeBasedSlots.morning.noPenalty.length +
      timeBasedSlots.morning.mediumPenalty.length +
      timeBasedSlots.morning.highPenalty.length,
    afternoon:
      timeBasedSlots.afternoon.noPenalty.length +
      timeBasedSlots.afternoon.mediumPenalty.length +
      timeBasedSlots.afternoon.highPenalty.length,
    evening:
      timeBasedSlots.evening.noPenalty.length +
      timeBasedSlots.evening.mediumPenalty.length +
      timeBasedSlots.evening.highPenalty.length,
  }
}

/**
 * Checks if a provider has any available slots
 */
export const hasAvailableSlots = (provider: EnhancedProvider): boolean => {
  return (
    provider.noPenaltySlots.length > 0 ||
    provider.mediumPenaltySlots.length > 0 ||
    provider.highPenaltySlots.length > 0
  )
}

/**
 * Gets slots for a specific penalty level from time-based slots
 */
export const getSlotsByPenaltyLevel = (
  timeBasedSlots: TimeBasedSlotData,
  penaltyLevel: PenaltyLevel
): { morning: Slot[]; afternoon: Slot[]; evening: Slot[] } => {
  // Map penalty level to property name
  const propertyName =
    penaltyLevel === PENALTY_LEVELS.NO_PENALTY
      ? 'noPenalty'
      : penaltyLevel === PENALTY_LEVELS.MEDIUM_PENALTY
      ? 'mediumPenalty'
      : 'highPenalty'

  return {
    morning: timeBasedSlots.morning[propertyName],
    afternoon: timeBasedSlots.afternoon[propertyName],
    evening: timeBasedSlots.evening[propertyName],
  }
}

/**
 * Creates time period counts array for slot selection
 */
export const createTimePeriodCounts = (slots: {
  morning: Slot[]
  afternoon: Slot[]
  evening: Slot[]
}): Array<{ period: string; count: number; slots: Slot[] }> => {
  return [
    { period: 'morning', count: slots.morning.length, slots: slots.morning },
    {
      period: 'afternoon',
      count: slots.afternoon.length,
      slots: slots.afternoon,
    },
    { period: 'evening', count: slots.evening.length, slots: slots.evening },
  ]
    .filter((tp) => tp.count > 0)
    .sort((a, b) => b.count - a.count)
}

/**
 * Calculates total slots across all time periods
 */
export const getTotalSlots = (slots: {
  morning: Slot[]
  afternoon: Slot[]
  evening: Slot[]
}): number => {
  return slots.morning.length + slots.afternoon.length + slots.evening.length
}

/**
 * Checks if current slots have time diversity (morning, afternoon, evening)
 */
export const hasTimeDiversity = (
  slots: Slot[],
  timeBasedSlots: TimeBasedSlotData
): {
  hasMorning: boolean
  hasAfternoon: boolean
  hasEvening: boolean
} => {
  const morningSlots = timeBasedSlots.morning.noPenalty.concat(
    timeBasedSlots.morning.mediumPenalty,
    timeBasedSlots.morning.highPenalty
  )
  const afternoonSlots = timeBasedSlots.afternoon.noPenalty.concat(
    timeBasedSlots.afternoon.mediumPenalty,
    timeBasedSlots.afternoon.highPenalty
  )
  const eveningSlots = timeBasedSlots.evening.noPenalty.concat(
    timeBasedSlots.evening.mediumPenalty,
    timeBasedSlots.evening.highPenalty
  )

  return {
    hasMorning: slots.some((slot) => morningSlots.includes(slot)),
    hasAfternoon: slots.some((slot) => afternoonSlots.includes(slot)),
    hasEvening: slots.some((slot) => eveningSlots.includes(slot)),
  }
}

/**
 * Tries to add time diversity by adding slots for missing time periods
 * Only adds slots from higher penalty levels if no lower penalty slots are available
 */
export const addTimeDiversity = (
  slots: Slot[],
  usedPenaltyLevels: string[],
  timeBasedSlots: TimeBasedSlotData
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const result = addTimeDiversityPenaltyLevel(
    slots,
    usedPenaltyLevels,
    timeBasedSlots,
    PENALTY_LEVELS.NO_PENALTY,
    SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
  )
  const diversity = hasTimeDiversity(result.slots, timeBasedSlots)

  if (diversity.hasMorning && diversity.hasAfternoon && diversity.hasEvening) {
    return result
  }

  return addTimeDiversityPenaltyLevel(
    result.slots,
    result.usedPenaltyLevels,
    timeBasedSlots,
    PENALTY_LEVELS.MEDIUM_PENALTY,
    SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
  )
}

export const addTimeDiversityPenaltyLevel = (
  slots: Slot[],
  usedPenaltyLevels: string[],
  timeBasedSlots: TimeBasedSlotData,
  targetPenaltyLevel: PenaltyLevel,
  maxSlots: number = SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const diversity = hasTimeDiversity(slots, timeBasedSlots)

  // Check if we should add slots from this penalty level
  // Only add if there are no lower penalty slots available for the missing time periods
  const shouldAddSlots = shouldAddPenaltyLevelSlots(
    timeBasedSlots,
    targetPenaltyLevel
  )

  if (!shouldAddSlots) {
    return { slots, usedPenaltyLevels }
  }

  const targetSlots = getSlotsByPenaltyLevel(timeBasedSlots, targetPenaltyLevel)

  const updatedSlots = [...slots]
  const updatedUsedPenaltyLevels = [...usedPenaltyLevels]

  // Try to add missing time periods (don't replace existing slots)
  const replacedIndices = new Set<number>()

  // Helper function to add a slot for a missing time period
  const addSlotForTimePeriod = (
    hasTimePeriod: boolean,
    timePeriodSlots: Slot[]
  ) => {
    timePeriodSlots.sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
    )

    if (!hasTimePeriod && timePeriodSlots.length > 0) {
      const availableSlot = timePeriodSlots.find(
        (slot) => !updatedSlots.includes(slot)
      )

      if (availableSlot) {
        // Find a slot to replace (preferably from the end, but not already replaced)
        let replaceIndex = updatedSlots.length - 1

        while (replacedIndices.has(replaceIndex) && replaceIndex > 0) {
          replaceIndex--
        }

        updatedSlots.splice(replaceIndex, 1, availableSlot)
        replacedIndices.add(replaceIndex)

        if (!updatedUsedPenaltyLevels.includes(targetPenaltyLevel)) {
          updatedUsedPenaltyLevels.push(targetPenaltyLevel)
        }
      }
    }
  }

  // Add slots for missing time periods
  addSlotForTimePeriod(diversity.hasMorning, targetSlots.morning)
  addSlotForTimePeriod(diversity.hasAfternoon, targetSlots.afternoon)
  addSlotForTimePeriod(diversity.hasEvening, targetSlots.evening)

  // If we still need more slots, add more from the time period with the most slots
  const remainingSlots = maxSlots - updatedSlots.length

  if (remainingSlots > 0) {
    const timePeriodCounts = createTimePeriodCounts(targetSlots)

    if (timePeriodCounts.length > 0) {
      const primaryPeriod = timePeriodCounts[0]
      const availableSlots = primaryPeriod.slots.filter(
        (slot) => !updatedSlots.includes(slot)
      )

      const slotsToAdd = Math.min(remainingSlots, availableSlots.length)
      updatedSlots.push(...availableSlots.slice(0, slotsToAdd))

      if (
        slotsToAdd > 0 &&
        !updatedUsedPenaltyLevels.includes(targetPenaltyLevel)
      ) {
        updatedUsedPenaltyLevels.push(targetPenaltyLevel)
      }
    }
  }

  return { slots: updatedSlots, usedPenaltyLevels: updatedUsedPenaltyLevels }
}

/**
 * Determines if we should add slots from a specific penalty level
 * For time diversity, we allow higher penalty slots to fill missing time periods
 */
export const shouldAddPenaltyLevelSlots = (
  timeBasedSlots: TimeBasedSlotData,
  targetPenaltyLevel: PenaltyLevel
): boolean => {
  // Always allow no-penalty slots
  if (targetPenaltyLevel === PENALTY_LEVELS.NO_PENALTY) {
    return true
  }

  // For medium penalty slots, allow them to be shown for time diversity
  // They will be filtered out in the actual selection logic if not needed
  if (targetPenaltyLevel === PENALTY_LEVELS.MEDIUM_PENALTY) {
    return true
  }

  // For high penalty slots, only show if no lower penalty slots are available
  if (targetPenaltyLevel === PENALTY_LEVELS.HIGH_PENALTY) {
    const noPenaltySlots = getSlotsByPenaltyLevel(
      timeBasedSlots,
      PENALTY_LEVELS.NO_PENALTY
    )
    const mediumPenaltySlots = getSlotsByPenaltyLevel(
      timeBasedSlots,
      PENALTY_LEVELS.MEDIUM_PENALTY
    )

    const totalLowerPenaltySlots =
      getTotalSlots(noPenaltySlots) + getTotalSlots(mediumPenaltySlots)

    return totalLowerPenaltySlots === 0
  }

  return true
}

/**
 * Selects slots with 4-1-1 time distribution for a given penalty level
 */
export const selectSlotsWithTimeDistribution = (
  timeBasedSlots: TimeBasedSlotData,
  penaltyLevel: PenaltyLevel,
  maxSlots: number = SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const slots = getSlotsByPenaltyLevel(timeBasedSlots, penaltyLevel)
  const timePeriodCounts = createTimePeriodCounts(slots)

  if (timePeriodCounts.length === 0) {
    return { slots: [], usedPenaltyLevels: [] }
  }

  const selectedSlots: Slot[] = []
  const usedPenaltyLevels = [penaltyLevel]

  // Step 1: Always prioritize morning slots first (earliest times)
  const morningPeriod = timePeriodCounts.find((tp) => tp.period === 'morning')

  if (morningPeriod && morningPeriod.count > 0) {
    // Sort morning slots by start time and select the earliest ones
    const sortedMorningSlots = morningPeriod.slots.sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
    )
    const morningSlotsToTake = Math.min(6, morningPeriod.count, maxSlots)
    selectedSlots.push(...sortedMorningSlots.slice(0, morningSlotsToTake))
  }

  // Step 2: Add time diversity by adding slots from other time periods
  const remainingSlots = maxSlots - selectedSlots.length

  if (remainingSlots > 0) {
    // Add 1 slot from afternoon if available (earliest time)
    const afternoonPeriod = timePeriodCounts.find(
      (tp) => tp.period === 'afternoon'
    )

    if (
      afternoonPeriod &&
      afternoonPeriod.count > 0 &&
      selectedSlots.length < maxSlots
    ) {
      const availableAfternoonSlots = afternoonPeriod.slots.filter(
        (slot) => !selectedSlots.includes(slot)
      )

      if (availableAfternoonSlots.length > 0) {
        // Sort by start time and select the earliest
        const earliestAfternoon = availableAfternoonSlots.sort(
          (a, b) =>
            new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
        )[0]
        selectedSlots.push(earliestAfternoon)
      }
    }

    // Add 1 slot from evening if available (earliest time)
    const eveningPeriod = timePeriodCounts.find((tp) => tp.period === 'evening')

    if (
      eveningPeriod &&
      eveningPeriod.count > 0 &&
      selectedSlots.length < maxSlots
    ) {
      const availableEveningSlots = eveningPeriod.slots.filter(
        (slot) => !selectedSlots.includes(slot)
      )

      if (availableEveningSlots.length > 0) {
        // Sort by start time and select the earliest
        const earliestEvening = availableEveningSlots.sort(
          (a, b) =>
            new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
        )[0]
        selectedSlots.push(earliestEvening)
      }
    }

    // If we still need more slots, fill from the time period with most slots
    if (selectedSlots.length < maxSlots) {
      const remainingSlotsNeeded = maxSlots - selectedSlots.length
      const primaryPeriod = timePeriodCounts[0] // Time period with most slots

      const availableSlots = primaryPeriod.slots.filter(
        (slot) => !selectedSlots.includes(slot)
      )

      const slotsToAdd = Math.min(remainingSlotsNeeded, availableSlots.length)
      selectedSlots.push(...availableSlots.slice(0, slotsToAdd))
    }
  }

  return { slots: selectedSlots, usedPenaltyLevels }
}

/**
 * Selects all slots for a given penalty level
 */
export const selectAllSlotsForPenaltyLevel = (
  timeBasedSlots: TimeBasedSlotData,
  penaltyLevel: PenaltyLevel
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const slots = getSlotsByPenaltyLevel(timeBasedSlots, penaltyLevel)
  const allSlots = [...slots.morning, ...slots.afternoon, ...slots.evening]

  return { slots: allSlots, usedPenaltyLevels: [penaltyLevel] }
}

/**
 * Selects slots from multiple penalty levels with time diversity
 * Only selects from penalty levels that should be shown based on hierarchy
 */
export const selectSlotsFromMultiplePenaltyLevels = (
  timeBasedSlots: TimeBasedSlotData,
  penaltyLevels: Array<PenaltyLevel>,
  maxSlots: number = SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const allSlots: Slot[] = []
  const usedPenaltyLevels: string[] = []

  for (const penaltyLevel of penaltyLevels) {
    // Only add slots from this penalty level if it should be shown
    if (shouldAddPenaltyLevelSlots(timeBasedSlots, penaltyLevel)) {
      const slots = getSlotsByPenaltyLevel(timeBasedSlots, penaltyLevel)
      const penaltySlots = [
        ...slots.morning,
        ...slots.afternoon,
        ...slots.evening,
      ]
      allSlots.push(...penaltySlots)

      if (penaltySlots.length > 0) {
        usedPenaltyLevels.push(penaltyLevel)
      }
    }
  }

  return {
    slots: allSlots.slice(0, maxSlots),
    usedPenaltyLevels,
  }
}

/**
 * Analyzes provider slots and determines the starting penalty level
 */
export const analyzeProviderSlots = (
  provider: EnhancedProvider,
  timeZone?: string
): ProviderSlotData => {
  const penaltyCounts: PenaltySlotCounts = {
    noPenaltiesLength: provider.noPenaltySlots.length,
    mediumPenaltiesLength: provider.mediumPenaltySlots.length,
    highPenaltiesLength: provider.highPenaltySlots.length,
  }

  // Categorize slots by time of day
  const timeBasedSlots = categorizeSlotsByTime(provider, timeZone)
  const timeOfDayCounts = getTimeOfDayCounts(timeBasedSlots)

  let refSlots: Slot[] = []
  let penaltyLevel: PenaltyLevel = PENALTY_LEVELS.NO_PENALTY

  if (penaltyCounts.noPenaltiesLength > 0) {
    refSlots = provider.noPenaltySlots
    penaltyLevel = PENALTY_LEVELS.NO_PENALTY
  } else if (penaltyCounts.mediumPenaltiesLength > 0) {
    refSlots = provider.mediumPenaltySlots
    penaltyLevel = PENALTY_LEVELS.MEDIUM_PENALTY
  } else if (penaltyCounts.highPenaltiesLength > 0) {
    refSlots = provider.highPenaltySlots
    penaltyLevel = PENALTY_LEVELS.HIGH_PENALTY
  }

  return {
    refSlots,
    penaltyLevel,
    penaltyCounts,
    timeBasedSlots,
    timeOfDayCounts,
  }
}

/**
 * Determines which slots to show based on the current phase
 * New time-based system that considers morning, afternoon, and evening slots
 */
export const getSlotsForPhase = (
  provider: EnhancedProvider,
  currentPhase: number,
  penaltyCounts: PenaltySlotCounts,
  timeBasedSlots: TimeBasedSlotData
): { slots: Slot[]; usedPenaltyLevels: string[] } => {
  const {
    noPenaltiesLength,
    mediumPenaltiesLength,
    highPenaltiesLength,
  } = penaltyCounts

  // Check if penalty filtering is disabled (feature flag off)
  // When disabled, all slots are in noPenaltySlots and medium/high are empty
  const isPenaltyFilteringDisabled =
    mediumPenaltiesLength === 0 &&
    highPenaltiesLength === 0 &&
    noPenaltiesLength > 0

  if (isPenaltyFilteringDisabled) {
    // Fallback to original behavior when penalty filtering is disabled
    switch (currentPhase) {
      case PHASES.LIMITED_SLOTS: {
        // Show first 6 slots
        return {
          slots: provider.noPenaltySlots.slice(0, 6),
          usedPenaltyLevels: [PENALTY_LEVELS.NO_PENALTY],
        }
      }

      case PHASES.ALL_CURRENT_LEVEL: {
        // Show all slots
        return {
          slots: [...provider.noPenaltySlots],
          usedPenaltyLevels: [PENALTY_LEVELS.NO_PENALTY],
        }
      }

      default:
        return {
          slots: [...provider.noPenaltySlots],
          usedPenaltyLevels: [PENALTY_LEVELS.NO_PENALTY],
        }
    }
  }

  // Determine the starting penalty level based on availability
  let startingLevel: PenaltyLevel

  if (noPenaltiesLength > 0) {
    startingLevel = PENALTY_LEVELS.NO_PENALTY
  } else if (mediumPenaltiesLength > 0) {
    startingLevel = PENALTY_LEVELS.MEDIUM_PENALTY
  } else {
    startingLevel = PENALTY_LEVELS.HIGH_PENALTY
  }

  switch (currentPhase) {
    case PHASES.LIMITED_SLOTS: {
      // Phase 1: Initial display based on starting penalty level and time of day

      if (startingLevel === PENALTY_LEVELS.NO_PENALTY) {
        // Get all no penalty slots by time of day
        const noPenaltySlots = getSlotsByPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.NO_PENALTY
        )
        const totalNoPenalty = getTotalSlots(noPenaltySlots)

        if (totalNoPenalty > SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT) {
          // 1. If >6 no penalty: Show 6 no penalty with 4-1-1 time distribution
          const result = selectSlotsWithTimeDistribution(
            timeBasedSlots,
            PENALTY_LEVELS.NO_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          // Try to add time diversity with no penalty slots from other time periods
          const diversityResult = addTimeDiversity(
            slots,
            usedPenaltyLevels,
            timeBasedSlots
          )

          const finalDiversityResult = {
            slots: diversityResult.slots.slice(
              0,
              SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
            ),
            usedPenaltyLevels: diversityResult.usedPenaltyLevels,
          }

          return finalDiversityResult
        } else {
          // 2. If 1-6 no penalty: Try 4-1-1 with no penalty, fill missing with medium penalty
          const result = selectSlotsWithTimeDistribution(
            timeBasedSlots,
            PENALTY_LEVELS.NO_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          // Try to add time diversity with medium penalty slots
          const diversityResult = addTimeDiversityPenaltyLevel(
            slots,
            usedPenaltyLevels,
            timeBasedSlots,
            PENALTY_LEVELS.NO_PENALTY
          )

          const finalDiversityResult = {
            slots: diversityResult.slots.slice(
              0,
              SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
            ),
            usedPenaltyLevels: diversityResult.usedPenaltyLevels,
          }

          return finalDiversityResult
        }
      } else if (startingLevel === PENALTY_LEVELS.MEDIUM_PENALTY) {
        // Medium penalty logic - use helper functions
        const mediumPenaltySlots = getSlotsByPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.MEDIUM_PENALTY
        )
        const totalMediumPenalty = getTotalSlots(mediumPenaltySlots)

        if (totalMediumPenalty > SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT) {
          // If >6 medium penalty: Show 6 medium penalty with 4-1-1 time distribution
          const result = selectSlotsWithTimeDistribution(
            timeBasedSlots,
            PENALTY_LEVELS.MEDIUM_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          // Try to add time diversity with high penalty slots (only if no lower penalty slots available)
          const diversityResult = addTimeDiversity(
            slots,
            usedPenaltyLevels,
            timeBasedSlots
          )

          const finalDiversityResult = {
            slots: diversityResult.slots.slice(
              0,
              SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
            ),
            usedPenaltyLevels: diversityResult.usedPenaltyLevels,
          }

          return finalDiversityResult
        } else {
          // If 1-6 medium penalty: Try 4-1-1 with medium penalty, fill missing with high penalty
          const result = selectSlotsWithTimeDistribution(
            timeBasedSlots,
            PENALTY_LEVELS.MEDIUM_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          // Try to add time diversity with high penalty slots (only if no lower penalty slots available)
          const diversityResult = addTimeDiversityPenaltyLevel(
            slots,
            usedPenaltyLevels,
            timeBasedSlots,
            PENALTY_LEVELS.MEDIUM_PENALTY
          )

          const finalDiversityResult = {
            slots: diversityResult.slots.slice(
              0,
              SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT
            ),
            usedPenaltyLevels: diversityResult.usedPenaltyLevels,
          }

          return finalDiversityResult
        }
      } else {
        // High penalty logic - use helper functions
        const highPenaltySlots = getSlotsByPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.HIGH_PENALTY
        )
        const totalHighPenalty = getTotalSlots(highPenaltySlots)

        if (totalHighPenalty > SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT) {
          // If >6 high penalty: Show 6 high penalty with 4-1-1 time distribution
          const result = selectSlotsWithTimeDistribution(
            timeBasedSlots,
            PENALTY_LEVELS.HIGH_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          const finalDiversityResult = {
            slots: slots.slice(0, SLOT_CONSTANTS.DEFAULT_SLOTS_COUNT),
            usedPenaltyLevels: usedPenaltyLevels,
          }

          return finalDiversityResult
        } else {
          // If 1-6 high penalty: Show all high penalty slots
          const result = selectAllSlotsForPenaltyLevel(
            timeBasedSlots,
            PENALTY_LEVELS.HIGH_PENALTY
          )
          const { slots, usedPenaltyLevels } = result

          const finalDiversityResult = {
            slots: slots,
            usedPenaltyLevels: usedPenaltyLevels,
          }

          return finalDiversityResult
        }
      }
    }

    case PHASES.ALL_CURRENT_LEVEL: {
      // Phase 2: After CTA click - expanded view
      // Show all available slots respecting penalty hierarchy

      if (startingLevel === PENALTY_LEVELS.NO_PENALTY) {
        // 1.2 & 2.2: Show all no penalty + medium penalty + high penalty slots
        const noPenaltyResult = selectAllSlotsForPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.NO_PENALTY
        )
        const mediumPenaltyResult = selectAllSlotsForPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.MEDIUM_PENALTY
        )

        const allSlots = [
          ...noPenaltyResult.slots,
          ...mediumPenaltyResult.slots,
        ]
        const usedPenaltyLevels = [
          ...noPenaltyResult.usedPenaltyLevels,
          ...mediumPenaltyResult.usedPenaltyLevels,
        ]

        return {
          slots: allSlots,
          usedPenaltyLevels: Array.from(new Set(usedPenaltyLevels)), // Remove duplicates
        }
      } else if (startingLevel === PENALTY_LEVELS.MEDIUM_PENALTY) {
        // 3.2 & 4.2: Show all medium penalty + high penalty slots
        const mediumPenaltyResult = selectAllSlotsForPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.MEDIUM_PENALTY
        )
        const highPenaltyResult = selectAllSlotsForPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.HIGH_PENALTY
        )

        const allSlots = [
          ...mediumPenaltyResult.slots,
          ...highPenaltyResult.slots,
        ]
        const usedPenaltyLevels = [
          ...mediumPenaltyResult.usedPenaltyLevels,
          ...highPenaltyResult.usedPenaltyLevels,
        ]

        return {
          slots: allSlots,
          usedPenaltyLevels: Array.from(new Set(usedPenaltyLevels)), // Remove duplicates
        }
      } else {
        // 5.2: Show all high penalty slots
        return selectAllSlotsForPenaltyLevel(
          timeBasedSlots,
          PENALTY_LEVELS.HIGH_PENALTY
        )
      }
    }

    default:
      // Fallback: show all available slots
      const slots = [
        ...timeBasedSlots.morning.noPenalty,
        ...timeBasedSlots.afternoon.noPenalty,
        ...timeBasedSlots.evening.noPenalty,
        ...timeBasedSlots.morning.mediumPenalty,
        ...timeBasedSlots.afternoon.mediumPenalty,
        ...timeBasedSlots.evening.mediumPenalty,
        ...timeBasedSlots.morning.highPenalty,
        ...timeBasedSlots.afternoon.highPenalty,
        ...timeBasedSlots.evening.highPenalty,
      ]
      return {
        slots,
        usedPenaltyLevels: [
          PENALTY_LEVELS.NO_PENALTY,
          PENALTY_LEVELS.MEDIUM_PENALTY,
          PENALTY_LEVELS.HIGH_PENALTY,
        ],
      }
  }
}

/**
 * Sorts slots by start time
 */
export const sortSlotsByTime = (slots: Slot[]): Slot[] => {
  return [...slots].sort(
    (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
  )
}

/**
 * Determines if there are more slots available for the next phase
 * Updated for the new 2-phase system
 */
export const hasMoreSlotsAvailable = (
  currentPhase: number,
  penaltyLevel: PenaltyLevel,
  penaltyCounts: PenaltySlotCounts
): boolean => {
  const {
    noPenaltiesLength,
    mediumPenaltiesLength,
    highPenaltiesLength,
  } = penaltyCounts

  // Check if penalty filtering is disabled (feature flag off)
  // When disabled, all slots are in noPenaltySlots and medium/high are empty
  const isPenaltyFilteringDisabled =
    mediumPenaltiesLength === 0 &&
    highPenaltiesLength === 0 &&
    noPenaltiesLength > 0

  if (isPenaltyFilteringDisabled) {
    // In Phase 1, show "More availability" if there are more than 6 slots
    // In Phase 2, don't show "More availability" (already showing all slots)
    if (currentPhase === PHASES.LIMITED_SLOTS) {
      return noPenaltiesLength > 6
    } else {
      return false // Phase 2 and beyond - no more slots to show
    }
  }

  // Determine the starting penalty level based on availability
  let startingLevel: 'no-penalty' | 'medium-penalty' | 'high-penalty'

  if (noPenaltiesLength > 0) {
    startingLevel = PENALTY_LEVELS.NO_PENALTY
  } else if (mediumPenaltiesLength > 0) {
    startingLevel = PENALTY_LEVELS.MEDIUM_PENALTY
  } else {
    startingLevel = PENALTY_LEVELS.HIGH_PENALTY
  }

  switch (currentPhase) {
    case PHASES.LIMITED_SLOTS: {
      // Phase 1: Check if there are more slots to show after CTA click

      if (startingLevel === PENALTY_LEVELS.NO_PENALTY) {
        // 1.1 & 2.1: Has more if there are medium penalty slots to show
        return mediumPenaltiesLength > 0
      } else if (startingLevel === PENALTY_LEVELS.MEDIUM_PENALTY) {
        // 3.1 & 4.1: Has more if there are high penalty slots to show
        return highPenaltiesLength > 0
      } else {
        // 5.1: Has more if there are more than 6 high penalty slots
        return highPenaltiesLength > 6
      }
    }

    case PHASES.ALL_CURRENT_LEVEL: {
      // Phase 2: After CTA click, no more slots to show
      return false
    }

    default:
      return false
  }
}

/**
 * Gets the appropriate button text for the current phase
 */
export const getButtonText = (phase: number): string => {
  const expandPhases: number[] = [
    PHASES.LIMITED_SLOTS,
    PHASES.ALL_CURRENT_LEVEL,
    PHASES.CURRENT_AND_NEXT_LEVEL,
  ]
  return expandPhases.includes(phase)
    ? BUTTON_TEXT.MORE_AVAILABILITY
    : BUTTON_TEXT.SHOW_LESS
}
